import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Button, Card, Space, Tag, Typography } from "antd";
import useScheduleDraftEditorWorkspace from "../../hooks/useScheduleDraftEditorWorkspace";
import MultitrackTimelineCanvas from "../../timeline/MultitrackTimelineCanvas";
import {
    TIMELINE_VIEWPORT_SYNC_MODE_ABSOLUTE,
    TIMELINE_VIEWPORT_SYNC_MODE_RELATIVE,
    buildBoundTimelineViewportRange,
    normalizeTimelineViewportRange,
} from "../../timeline/viewportBinding";

const { Text } = Typography;

const PATTERN_TYPE_LABELS = {
    weekly: "按周",
    rotation: "按轮换",
    demand: "按需求",
};

function getPatternTypeLabel(patternType) {
    return PATTERN_TYPE_LABELS[patternType] || "分段观察";
}

function toTime(value) {
    const time = value ? new Date(value).getTime() : NaN;
    return Number.isFinite(time) ? time : null;
}

function itemIntersectsWindow(item, timeWindow) {
    const windowStart = toTime(timeWindow?.start);
    const windowEnd = toTime(timeWindow?.end);
    if (windowStart === null || windowEnd === null || windowEnd <= windowStart) {
        return true;
    }

    const itemStart = toTime(item?.start);
    const itemEnd = toTime(item?.end) ?? itemStart;
    if (itemStart === null || itemEnd === null) {
        return false;
    }

    return itemStart <= windowEnd && itemEnd >= windowStart;
}

function isFocusedSegment(segment, timelineFocusWindow, patternType) {
    return Boolean(
        segment?.key
            && timelineFocusWindow?.segmentKey === segment.key
            && timelineFocusWindow?.patternType === patternType,
    );
}

function buildSegmentTimelineModel(canvasModel, segment) {
    if (!segment?.timeWindow) {
        return canvasModel;
    }

    return {
        ...canvasModel,
        items: (canvasModel?.items || []).filter((item) => itemIntersectsWindow(item, segment.timeWindow)),
        timeWindow: segment.timeWindow,
    };
}

function getSegmentViewportId(patternType, segmentKey) {
    return `timeline:${patternType}:${segmentKey}`;
}

function getFirstTimelineInstance(instanceMap) {
    return instanceMap.values().next().value || null;
}

const TimelineSegmentStack = ({ canvasModel, patternModel, patternType, timelineFocusWindow, onSelectItem, onHoverItem, onTrackAction, timelineRef }) => {
    const segmentTimelineRefs = useRef(new Map());
    const segmentPanelRefs = useRef(new Map());
    const segmentViewportMetasRef = useRef(new Map());
    const isViewportSyncingRef = useRef(false);
    const segments = useMemo(() => patternModel?.segments || [], [patternModel?.segments]);
    const activeSegmentKey = timelineFocusWindow?.patternType === patternType ? timelineFocusWindow?.segmentKey : null;
    const segmentModels = useMemo(
        () => segments.map((segment) => ({ segment, model: buildSegmentTimelineModel(canvasModel, segment) })),
        [canvasModel, segments],
    );
    const segmentViewportMetas = useMemo(() => new Map(segmentModels.map(({ segment }) => {
        const viewportId = getSegmentViewportId(patternType, segment.key);
        return [viewportId, {
            id: viewportId,
            groupId: `timeline-segments:${patternType}`,
            syncMode: segment.timeWindow?.start ? TIMELINE_VIEWPORT_SYNC_MODE_RELATIVE : TIMELINE_VIEWPORT_SYNC_MODE_ABSOLUTE,
            segmentKey: segment.key,
            segmentAnchor: segment.timeWindow?.start || null,
            timeWindow: segment.timeWindow || null,
        }];
    })), [patternType, segmentModels]);

    segmentViewportMetasRef.current = segmentViewportMetas;

    const handleSegmentViewportRangeChange = useCallback((event) => {
        if (isViewportSyncingRef.current) {
            return;
        }

        const sourceId = event?.viewportId;
        const sourceMeta = sourceId ? segmentViewportMetasRef.current.get(sourceId) : null;
        const sourceRange = normalizeTimelineViewportRange(event?.range);
        if (!sourceId || !sourceMeta || !sourceRange) {
            return;
        }

        isViewportSyncingRef.current = true;
        try {
            segmentTimelineRefs.current.forEach((instance, targetId) => {
                if (targetId === sourceId) {
                    return;
                }
                const targetMeta = segmentViewportMetasRef.current.get(targetId);
                const targetRange = buildBoundTimelineViewportRange(sourceRange, sourceMeta, targetMeta);
                if (targetRange) {
                    instance?.setWindow?.(targetRange, { sourceId, source: event.source });
                }
            });
        } finally {
            isViewportSyncingRef.current = false;
        }
    }, []);

    useEffect(() => {
        if (!timelineRef) {
            return undefined;
        }

        timelineRef.current = {
            zoomIn: () => getFirstTimelineInstance(segmentTimelineRefs.current)?.zoomIn?.(),
            zoomOut: () => getFirstTimelineInstance(segmentTimelineRefs.current)?.zoomOut?.(),
            fit: () => {
                isViewportSyncingRef.current = true;
                try {
                    segmentTimelineRefs.current.forEach((instance) => instance?.fit?.());
                } finally {
                    isViewportSyncingRef.current = false;
                }
            },
            redraw: () => segmentTimelineRefs.current.forEach((instance) => instance?.redraw?.()),
            clearPreview: () => segmentTimelineRefs.current.forEach((instance) => instance?.clearPreview?.()),
            setWindow: (range, options) => segmentTimelineRefs.current.forEach((instance) => instance?.setWindow?.(range, options)),
            getWindow: () => getFirstTimelineInstance(segmentTimelineRefs.current)?.getWindow?.(),
        };

        return () => {
            if (timelineRef.current) {
                timelineRef.current = null;
            }
        };
    }, [timelineRef]);

    useEffect(() => {
        if (!activeSegmentKey) {
            return;
        }

        segmentPanelRefs.current.get(activeSegmentKey)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, [activeSegmentKey]);

    if (!segments.length) {
        return null;
    }

    const setTimelineInstance = (viewportId) => (instance) => {
        if (instance) {
            segmentTimelineRefs.current.set(viewportId, instance);
            return;
        }

        segmentTimelineRefs.current.delete(viewportId);
    };

    const setPanelElement = (segmentKey) => (element) => {
        if (element) {
            segmentPanelRefs.current.set(segmentKey, element);
            return;
        }

        segmentPanelRefs.current.delete(segmentKey);
    };

    return (
        <div className="schedule-draft-timeline-segment-stack" data-testid="schedule-draft-timeline-segment-stack">
            {segmentModels.map(({ segment, model }) => {
                const active = isFocusedSegment(segment, timelineFocusWindow, patternType);
                const viewportId = getSegmentViewportId(patternType, segment.key);
                const viewportMeta = segmentViewportMetas.get(viewportId);
                return (
                    <div
                        key={segment.key}
                        ref={setPanelElement(segment.key)}
                        className={`schedule-draft-timeline-segment-panel ${active ? "schedule-draft-timeline-segment-panel-active" : ""}`}
                        data-testid="schedule-draft-timeline-segment-panel"
                    >
                        <div className="schedule-draft-timeline-segment-corner" aria-label={`${segment.title}，${segment.metrics?.flightCount || 0} 航班`}>
                            <Text strong className="schedule-draft-timeline-segment-corner-title">{segment.title}</Text>
                            <Text className="schedule-draft-timeline-segment-corner-meta">
                                {`${segment.metrics?.flightCount || 0} 航班`}
                            </Text>
                            {active ? <span className="schedule-draft-timeline-segment-corner-dot" /> : null}
                        </div>

                        <MultitrackTimelineCanvas
                            ref={setTimelineInstance(viewportId)}
                            model={model}
                            onSelectItem={onSelectItem}
                            onHoverItem={onHoverItem}
                            onTrackAction={onTrackAction}
                            viewportBinding={{ ...viewportMeta, onRangeChange: handleSegmentViewportRangeChange }}
                            itemPreviewTrigger="click"
                        />
                    </div>
                );
            })}
        </div>
    );
};

const ScheduleDraftTimelineSection = ({ timelineRef, observationControls, onHoverTargetChange, onTrackAction, timelineCanvasCollapsed = false, onTimelineCanvasCollapsedChange, timelineRangeMode = "full" }) => {
    const {
        data: { canvasModel, patternModel },
        ui: { handleSelectItem, patternType, timelineFocusWindow },
    } = useScheduleDraftEditorWorkspace();
    const hasSegments = Boolean(patternModel?.segments?.length);
    const canRenderWeeklySegments = patternType === "weekly" && hasSegments;
    const shouldRenderWeeklySegments = !timelineCanvasCollapsed && timelineRangeMode === "weekly" && canRenderWeeklySegments;
    const handleHoverItem = (item, hoverContext = {}) => {
        onHoverTargetChange?.(item ? { mode: "timeline", item, ...hoverContext } : null);
    };
    const statusTags = [
        hasSegments ? <Tag key="segments" color="purple">{`${getPatternTypeLabel(patternType)} · ${patternModel.segments.length} 段`}</Tag> : null,
        timelineFocusWindow ? <Tag key="focus" color="gold">{`定位: ${timelineFocusWindow.label}`}</Tag> : null,
    ].filter(Boolean);

    useEffect(() => {
        if (!timelineCanvasCollapsed) {
            return;
        }
        onHoverTargetChange?.(null);
        timelineRef?.current?.clearPreview?.();
        if (timelineRef) {
            timelineRef.current = null;
        }
    }, [onHoverTargetChange, timelineCanvasCollapsed, timelineRef]);

    return (
        <Card className="schedule-draft-main-card schedule-draft-timeline-card" bordered={false}>
            <div className="schedule-draft-main-card-header schedule-draft-observation-header">
                <div className="schedule-draft-observation-header-actions schedule-draft-observation-header-actions-primary">{observationControls}</div>
                {statusTags.length ? (
                    <div className="schedule-draft-section-heading schedule-draft-observation-status-tags">
                        <Space wrap size={[8, 8]}>
                            {statusTags}
                        </Space>
                    </div>
                ) : null}
            </div>

            {timelineCanvasCollapsed ? (
                <div className="schedule-draft-timeline-collapsed-state" data-testid="schedule-draft-timeline-collapsed-state">
                    <div className="schedule-draft-timeline-collapsed-copy">
                        <Text strong>时间轴画布已隐藏</Text>
                        <Text type="secondary">已保留 Timetable 编辑区；恢复后会重新加载时间线画布。</Text>
                    </div>
                    <Button type="primary" size="small" onClick={() => onTimelineCanvasCollapsedChange?.(false)}>
                        显示时间轴
                    </Button>
                </div>
            ) : shouldRenderWeeklySegments ? (
                <TimelineSegmentStack
                    canvasModel={canvasModel}
                    patternModel={patternModel}
                    patternType={patternType}
                    timelineFocusWindow={timelineFocusWindow}
                    onSelectItem={handleSelectItem}
                    onHoverItem={handleHoverItem}
                    onTrackAction={onTrackAction}
                    timelineRef={timelineRef}
                />
            ) : (
                <MultitrackTimelineCanvas ref={timelineRef} model={canvasModel} onSelectItem={handleSelectItem} onHoverItem={handleHoverItem} onTrackAction={onTrackAction} itemPreviewTrigger="click" />
            )}
        </Card>
    );
};

export default ScheduleDraftTimelineSection;
